import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@material-ui/core';
import {
  MonetizationOn as MonetizationOnIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as EmojiEventsIcon,
  ExitToApp as ExitToAppIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAuth } from 'providers/auth';
import { LOGOUT_MUTATION } from 'graphql/mutations/auth';
import { AUTH_TOKEN, REST_TOKEN } from 'utils/constants';
import getInitials from 'helpers/getInitials';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f8f9fa',
    minHeight: '100vh',
    paddingBottom: theme.spacing(8),
  },
  hero: {
    maxWidth: 700,
    margin: '64px auto 0',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  iconMain: {
    fontSize: 60,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  features: {
    maxWidth: 1100,
    margin: '80px auto 0',
  },
  featureIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(2),
  },
  quoteSection: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(6),
    textAlign: 'center',
  },
  quoteCard: {
    margin: theme.spacing(2),
    minWidth: 280,
    maxWidth: 350,
    display: 'inline-block',
    verticalAlign: 'top',
  },
  footer: {
    textAlign: 'center',
    marginTop: theme.spacing(10),
    color: theme.palette.text.secondary,
  },
  appBar: {
    boxShadow: 'none',
    background: '#fff',
  },
  toolbarButtons: {
    marginLeft: 'auto',
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  profileAvatar: {
    width: 40,
    height: 40,
    cursor: 'pointer',
    fontSize: '1rem',
    backgroundColor: theme.palette.primary.main,
  },
  profileName: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontWeight: 'bold',
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    minWidth: 200,
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
}));

const quotes = [
  {
    author: 'George Lorimer',
    text: 'It’s good to have money and the things that money can buy, but it’s good, too, to check up once in a while and make sure that you haven’t lost the things that money can’t buy.',
  },
  {
    author: 'Johann Wolfgang von Goethe',
    text: 'Many people take no care of their money till they come nearly to the end of it, and others do just the same with their time.',
  },
  {
    author: 'Anonymous',
    text: 'Discipline is the bridge between goals and accomplishment.',
  },
];

const ProfileDropdown = ({ user, onLogout }) => {
  const classes = useStyles();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    history.push('/settings');
    handleClose();
  };

  const handleLogout = () => {
    onLogout();
    handleClose();
  };

  return (
    <div className={classes.profileSection}>
      <Avatar
        className={classes.profileAvatar}
        onClick={handleClick}
        src={user.avatar || '/images/user_profile.png'}
      >
        {!user.avatar && getInitials(user.firstName || user.email)}
      </Avatar>
      <Typography className={classes.profileName} variant="subtitle1">
        {user.firstName || user.email}
      </Typography>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleSettings} className={classes.menuItem}>
          <SettingsIcon className={classes.menuIcon} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} className={classes.menuItem}>
          <ExitToAppIcon className={classes.menuIcon} />
          Logout
        </MenuItem>
      </Menu>
    </div>
  );
};

export const Main = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, isAuthenticated, setAuthUser } = useAuth();
  const [logoutUser, { data }] = useMutation(LOGOUT_MUTATION, {
    errorPolicy: 'ignore',
  });

  React.useEffect(() => {
    if (data && data.logoutUser) {
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(REST_TOKEN);
      setAuthUser(null);
      // Stay on the home page instead of redirecting
    }
  }, [data, setAuthUser]);

  const handleLogin = () => {
    history.push('/sign-in');
  };

  const handleLogout = () => {
    logoutUser();
  };

  return (
    <Box className={classes.root}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <Typography
            variant="h6"
            color="primary"
            style={{ fontWeight: 'bold' }}
          >
            D2D Finance
          </Typography>
          {isAuthenticated ? (
            <ProfileDropdown user={user} onLogout={handleLogout} />
          ) : (
            <Box className={classes.toolbarButtons}>
              <Button
                color="primary"
                variant="outlined"
                style={{ marginRight: 12 }}
                onClick={handleLogin}
              >
                Log In
              </Button>
              <Button color="primary" variant="contained" onClick={handleLogin}>
                Get Started
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box className={classes.hero}>
        <MonetizationOnIcon className={classes.iconMain} />
        <Typography variant="h3" style={{ fontWeight: 'bold' }} gutterBottom>
          Take Control of Your Money
        </Typography>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Track your expenses and income, build healthy habits, and achieve your
          financial goals — all in one app.
        </Typography>
        <Button
          size="large"
          color="primary"
          variant="contained"
          style={{
            marginTop: 32,
            padding: '12px 40px',
            fontWeight: 'bold',
            fontSize: 18,
          }}
          onClick={
            isAuthenticated ? () => history.push('/dashboard') : handleLogin
          }
        >
          {isAuthenticated ? 'Go to Dashboard' : 'Start Tracking Your Money'}
        </Button>
      </Box>

      <Box className={classes.features}>
        <Grid container spacing={4} justify="center" alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent style={{ textAlign: 'center' }}>
                <TrendingUpIcon
                  color="primary"
                  className={classes.featureIcon}
                />
                <Typography
                  variant="h6"
                  style={{ fontWeight: 'bold' }}
                  gutterBottom
                >
                  Smart Insights
                </Typography>
                <Typography color="textSecondary">
                  Visualize your spending, spot trends, and make informed
                  decisions with easy-to-understand charts and stats.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent style={{ textAlign: 'center' }}>
                <MonetizationOnIcon
                  style={{ color: '#2e7d32' }}
                  className={classes.featureIcon}
                />
                <Typography
                  variant="h6"
                  style={{ fontWeight: 'bold' }}
                  gutterBottom
                >
                  Easy Tracking
                </Typography>
                <Typography color="textSecondary">
                  Log expenses and income in seconds — anytime, anywhere. Stay
                  on top of your money effortlessly.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent style={{ textAlign: 'center' }}>
                <EmojiEventsIcon
                  style={{ color: '#ffb300' }}
                  className={classes.featureIcon}
                />
                <Typography
                  variant="h6"
                  style={{ fontWeight: 'bold' }}
                  gutterBottom
                >
                  Build Great Habits
                </Typography>
                <Typography color="textSecondary">
                  Set goals, get reminders, and celebrate milestones as you
                  build financial discipline and grow your savings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.quoteSection}>
        <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
          Words to Inspire Your Financial Journey
        </Typography>
        {quotes.map((q, i) => (
          <Card key={i} className={classes.quoteCard}>
            <CardContent>
              <Typography variant="body1" gutterBottom>
                “{q.text}”
              </Typography>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="right"
              >
                — {q.author}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box className={classes.footer}>
        <Typography variant="body2">
          © {new Date().getFullYear()} D2D Finance — Build a better financial
          future.
        </Typography>
      </Box>
    </Box>
  );
};
